const AdminUserForm = ({
  form,
  roles,
  editing,
  saving,
  onChange,
  onToggleRole,
  onSubmit,
  onCancel,
}) => {
  return (
    <form onSubmit={onSubmit} className="rounded-[28px] border border-[color:var(--admin-border)] bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-[color:var(--admin-icon)]">Admin Form</p>
          <h2 className="mt-2 text-lg font-semibold text-[color:var(--admin-heading)]">
            {editing ? 'Edit Admin' : 'Add Admin'}
          </h2>
        </div>
        <div className="flex gap-2">
          {editing && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full border border-[color:var(--admin-border)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--admin-text)]"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-[var(--admin-accent)] px-5 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white disabled:opacity-60"
          >
            {saving ? 'Saving...' : editing ? 'Update Admin' : 'Create Admin'}
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">Full name</label>
          <input
            value={form.fullName}
            onChange={(event) => onChange('fullName', event.target.value)}
            className="mt-2 w-full rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">Email</label>
          <input
            value={form.email}
            onChange={(event) => onChange('email', event.target.value)}
            className="mt-2 w-full rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">
            {editing ? 'Temporary password' : 'Password'}
          </label>
          <input
            type="password"
            value={form.temporaryPassword}
            onChange={(event) => onChange('temporaryPassword', event.target.value)}
            placeholder={editing ? 'Leave blank to keep current password' : 'Set temporary password'}
            className="mt-2 w-full rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">Admin type</label>
          <select
            value={form.adminType}
            onChange={(event) => onChange('adminType', event.target.value)}
            className="mt-2 w-full rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)]"
          >
            <option value="SUB_ADMIN">SUB_ADMIN</option>
            <option value="MASTER_ADMIN">MASTER_ADMIN</option>
          </select>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <label className="flex items-center gap-3 rounded-2xl border border-[color:var(--admin-border)] bg-[var(--admin-muted-surface)] px-4 py-3 text-sm text-[color:var(--admin-heading)]">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => onChange('isActive', event.target.checked)}
          />
          Active status
        </label>
        <label className="flex items-center gap-3 rounded-2xl border border-[color:var(--admin-border)] bg-[var(--admin-muted-surface)] px-4 py-3 text-sm text-[color:var(--admin-heading)]">
          <input
            type="checkbox"
            checked={form.mustChangePassword}
            onChange={(event) => onChange('mustChangePassword', event.target.checked)}
          />
          Must change password
        </label>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[color:var(--admin-icon)]">Assigned roles</p>
            <p className="mt-1 text-sm text-[color:var(--admin-text)]">
              Master Admin ignores role restrictions. Sub Admins require at least one role.
            </p>
          </div>
        </div>
        <div className="mt-3 grid gap-3 xl:grid-cols-2">
          {roles.map((role) => {
            const checked = form.roleIds.includes(role.id);
            const disabled = form.adminType === 'MASTER_ADMIN';
            return (
              <label
                key={role.id}
                className={`rounded-2xl border px-4 py-3 ${
                  checked ? 'border-[var(--admin-accent)] bg-[rgba(15,92,90,0.06)]' : 'border-[color:var(--admin-border)] bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={disabled ? role.roleCode === 'MASTER_ADMIN' : checked}
                    disabled={disabled}
                    onChange={() => onToggleRole(role.id)}
                    className="mt-1"
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[color:var(--admin-heading)]">{role.roleName}</span>
                      <span className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${role.isSystemRole ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
                        {role.roleType}
                      </span>
                    </div>
                    {role.description && (
                      <p className="mt-1 text-sm text-[color:var(--admin-text)]">{role.description}</p>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </form>
  );
};

export default AdminUserForm;
